<template>
    <div class="flex flex-wrap box justify-between gap-y-3 gap-x-8 xl:gap-x-12">

        <div class="min-w-0">
            <div class="flex mb-2">
                <jet-label for="customer" value="Customer:" class="w-32 xl:w-20 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <div v-if="form.status === 2">
                        {{ selectedCustomer.name }}
                    </div>
                    <multiselect
                        v-else
                        class="multi-lg h-7 text-base"
                        v-model="form.customer_id"
                        :options="displayedCustomers"
                        valueProp="id"
                        trackBy="name"
                        label="name"
                        :searchable="true"
                        placeholder="Select Customer"
                        ref="customer"
                    />
                    <jet-input-error :message="$page.props.errors.customer_id" class="m1" />
                </div>
            </div>

            <template v-if="selectedCustomer">
                <div class="flex mb-2">
                    <jet-label for="address" value="Address:" class="w-32 xl:w-20 align-top leading-7" />
                    <div v-if="selectedCustomer" class="flex flex-1 flex-col">
                        <div>{{ selectedCustomer.address_1 }}</div>
                        <div>{{ selectedCustomer.address_2 }}</div>
                        <div>{{ selectedCustomer.city }} {{ selectedCustomer.province }}, {{ selectedCustomer.postal_code }}</div>
                    </div>
                </div>
                <div class="flex mb-2">
                    <jet-label value="PO #:" class="w-32 xl:w-20 align-top leading-7" />

                    <div class="flex-1">
                        <div v-if="form.status === 2">
                            {{ form.po_num }}
                        </div>
                        <jet-input v-else type="text" v-model="form.po_num" class="w-full"/>
                        <jet-input-error :message="$page.props.errors.po_num" class="mt-1" />
                    </div>
                </div>
                <div class="flex mb-2">
                    <jet-label for="phone" value="Phone:" class="w-32 xl:w-20 align-top leading-7" />
                    <div v-if="selectedCustomer">
                        {{ selectedCustomer.phone }}
                    </div>
                </div>
                <div class="flex items-start" v-if="selectedCustomer.emails.length">
                    <jet-label for="email" value="Email:" class="w-32 xl:w-20 align-top leading-7" />
                    <div class="flex flex-1">
                        <ul class="leading-tight">
                            <li v-for="email in selectedCustomer.emails" :key="email" class="break-all">{{ email }}</li>
                        </ul>
                    </div>
                </div>
            </template>
        </div>

        <div>
            <div class="flex mb-2">
                <jet-label for="order_num" value="Order #:" class="w-32 align-top leading-7" />
                <span>{{ form.id }}</span>
            </div>
            
            <div class="flex mb-2">
                <jet-label for="placed_by" value="Placed By:" class="w-32 align-top leading-7" />
                <span>{{ form.placed_by_name }}</span>
            </div>

            <div class="flex mb-2">
                <jet-label for="order_date" value="Order Date:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <div v-if="form.status === 2">
                        {{ formatDate(form.order_date) }}
                    </div>
                    <datepicker 
                        v-else
                        class="w-100"
                        inputClassName="dp-custom-input"
                        v-model="form.order_date"
                        weekStart="0"
                        monthNameFormat="long"
                        :enableTimePicker="false"
                        :autoApply="true"
                        :is24="false"
                        :clearable="false"
                        :maxDate="maxDate"
                        :format="formatDate"
                        position="right"
                        disabled
                    />
                    <jet-input-error :message="$page.props.errors.order_date" class="m-1" />
                </div>
            </div>

            <div class="flex mb-2">
                <jet-label for="shipping_method" value="Shipping Method:" class="w-32 align-top leading-7 whitespace-nowrap" />
                <div class="flex flex-1 flex-col">
                    <div v-if="form.status === 2">
                        {{ formatStatus(form.shipping_method) }}
                    </div>
                   <multiselect
                        v-else
                        class="multi-lg h-7 text-base"
                        v-model="form.shipping_method"
                        :options="shippingOptions"
                        valueProp="value"
                        trackBy="value"
                        label="label"
                        :canClear="false"
                        :canDeselect="false"
                        @select="checkDeliveryDays"
                    />
                    <jet-input-error :message="$page.props.errors.shipping_method" class="m1" />
                </div>
            </div>

            <div class="flex">
                <jet-label for="ship_date" value="Ship Date:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <div v-if="form.status === 2">
                        {{ formatDate(form.ship_date) }}
                    </div>
                    <datepicker
                        v-else
                        class="w-100"
                        inputClassName="dp-custom-input"
                        v-model="form.ship_date"
                        weekStart="0"
                        monthNameFormat="long"
                        :enableTimePicker="false"
                        :autoApply="true"
                        :is24="false"
                        :clearable="false"
                        :minDate="minDate"
                        :maxDate="maxDate"
                        :disabled-dates="customerDisabledDates"
                        :format="formatDate"
                        position="right"
                    />
                    <jet-input-error :message="$page.props.errors.ship_date" class="m-1" />

                    <div v-if="orderWillBeSplit && shipDateConstraints">
                        <p class="text-red font-bold text-xs mt-1">Special Order Items will be
                            {{ form.shipping_method === 'pickup' ? 'ready for pickup' : 'delivered' }}
                            on {{ shipDateConstraints.special_order_min_date }}
                        </p>
                    </div>

                    <div class="flex justify-between pt-3 xl:pt-1">
                        <jet-label for="override_dates" value="Override Shipping Dates:" class="text-sm" />
                        <jet-input
                            :disabled="form.status === 2"
                            type="checkbox"
                            v-model:checked="form.override_ship_date"
                            @click="toggleOverrideDates()"
                            class="h-5 w-5"
                            :title="form.id !== null ? 'Override cannot be changed after the order has been created.' : ''"
                        ></jet-input>
                    </div>
                </div>
            </div>
            
            <div class="flex mb-2" v-if="form.is_admin && selectedCustomerPayment">
                <jet-label for="payment_status" value="Payment Status:" class="w-32 align-top leading-7 whitespace-nowrap" />
                <div class="flex flex-1 flex-col">
                    <div v-if="form.status === 2">
                        {{ formatStatus(form.payment_status) }}
                    </div>
                   <multiselect 
                        v-else
                        class="multi-lg h-7 text-base"
                        v-model="form.payment_status"
                        :options="paymentOptions"
                        :canClear="false"
                        :canDeselect="false"
                    />
                    <jet-input-error :message="$page.props.errors.payment_status" class="m1" />
                </div>
            </div>

            <div class="flex mb-2" v-if="form.is_admin && form.status >= 2">
                <jet-label value="Payment Method:" class="w-32 align-top leading-7 whitespace-nowrap" />
                <div class="flex flex-1 flex-col">
                   <multiselect 
                        class="multi-lg h-7 text-base"
                        v-model="form.payment_method"
                        :options="paymentMethodOptions"
                        :canClear="false"
                        :canDeselect="false"
                    />
                    <jet-input-error :message="$page.props.errors.payment_status" class="m1" />
                </div>
            </div>

            <div class="flex mb-2" v-if="form.is_admin && form.status >= 2">
                <jet-label value="Payment Terms:" class="w-32 align-top leading-7 whitespace-nowrap" />
                <div class="flex flex-1 flex-col">
                   <multiselect 
                        class="multi-lg h-7 text-base"
                        v-model="form.payment_terms"
                        :options="paymentTermsOptions"
                        :canClear="false"
                        :canDeselect="false"
                    />
                    <jet-input-error :message="$page.props.errors.payment_status" class="m1" />
                </div>
            </div>
        </div>

        <div>
            <div class="flex mb-2">
                <jet-label for="internal_notes" value="Internal Notes:" class="w-32 align-top leading-7" />
                <div v-if="selectedCustomer" class="flex flex-1">
                    <div>{{selectedCustomer.internal_notes}}</div>
                </div>
            </div>

            <div class="flex mb-2">
                <jet-label for="notes" value="Shipping Notes:" class="w-32 align-top leading-7" />
                <div class="flex flex-1">
                    <div v-if="form.status === 2"> {{ form.notes }} </div>
                    <textarea
                        v-else
                        class="w-full resize-none"
                        v-model="form.notes"
                    ></textarea>
                </div>
            </div>

            <div v-if="can_edit_picking_notes" class="flex mb-2">
                <jet-label for="picking_notes" value="Picking Notes:" class="w-32 align-top leading-7" />
                <div class="flex flex-1">
                    <textarea
                        class="w-full resize-none"
                        v-model="form.picking_notes"
                    ></textarea>
                    <jet-input-error :message="$page.props.errors.picking_notes" class="mt-1" />
                </div>
            </div>
            <div v-else-if="form.picking_notes" class="flex mb-2">
                <jet-label for="picking_notes" value="Picking Notes:" class="w-32 align-top leading-7" />
                <div class="flex flex-1">{{ form.picking_notes }}</div>
            </div>

            <div class="flex mb-2">
                <jet-label for="load_notes" value="Load Sheet Notes:" class="w-32 align-top leading-7" />
                <div v-if="selectedCustomer" class="flex flex-1">
                    <div>{{selectedCustomer.load_notes}}</div>
                </div>
            </div>

            <div class="flex mb-2">
                <jet-label for="skills" value="Skills:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <div v-if="form.status === 2">{{ (form.skills || []).join(', ') }}</div>
                    <multiselect
                        v-else
                        v-model="form.skills"
                        :options="skillOptions"
                        mode="tags"
                        :createTag="true"
                        :closeOnSelect="false"
                        class="multi-lg"
                        placeholder="Skills"
                    />
                    <jet-input-error :message="$page.props.errors.skills" class="mt-1" />
                </div>
            </div>

            <div class="flex mb-2">
                <jet-label for="vehicle_features" value="Vehicle Type:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <div v-if="form.status === 2">{{ (form.vehicle_features || []).join(', ') }}</div>
                    <multiselect
                        v-else
                        v-model="form.vehicle_features"
                        :options="vehicleFeatureOptions"
                        mode="tags"
                        :createTag="true"
                        :closeOnSelect="false"
                        class="multi-lg"
                        placeholder="Vehicle type"
                    />
                    <jet-input-error :message="$page.props.errors.vehicle_features" class="mt-1" />
                </div>
            </div>

            <div class="flex mb-2">
                <jet-label for="priority" value="Priority:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 flex-col">
                    <div v-if="form.status === 2">M</div>
                    <multiselect
                        v-else
                        v-model="form.priority"
                        :options="priorityOptions"
                        :canClear="false"
                        :disabled="true"
                        class="multi-lg"
                    />
                    <jet-input-error :message="$page.props.errors.priority" class="mt-1" />
                </div>
            </div>

            <div v-if="form.optimoroute_error" class="flex mb-2">
                <jet-label for="optimoroute_error" value="OptimoRoute Error:" class="w-32 align-top leading-7" />
                <div class="flex flex-1 text-red-600 text-sm">{{ form.optimoroute_error }}</div>
            </div>
        </div>

    </div>
</template>

<script>
import Datepicker from '@vuepic/vue-datepicker';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import Multiselect from '@vueform/multiselect';
import { DateTime } from 'luxon';
import { usePage } from '@inertiajs/inertia-vue3';
import { fetchStaffShipDateConstraints } from '@/Composables/useWholesaleShipDateConstraints';

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    components: {
        Datepicker,
        JetInput,
        JetInputError,
        JetLabel,
        Multiselect,
    },

    props: ['customers', 'categories', 'holidays', 'errors', 'form', 'can_edit_picking_notes'],

    setup() {
        return { page: usePage() };
    },

    data() {
        return{
            displayedCustomers: this.customers.map( i => {
                return {
                    ...i,
                    disabled: !i.enabled
                }
            }),

            shippingOptions: [
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
            ],

            paymentOptions: [
                { value: 'paid', label: 'Paid' },
                { value: 'unpaid', label: 'Unpaid' },
            ],

            paymentMethodOptions: [
                { value: 'credit_card', label: 'Credit Card' },
                { value: 'credit_card_manual', label: 'Credit Card - Manual' },
                { value: 'e_transfer', label: 'e-Transfer' },
                { value: 'cheque', label: 'Cheque' },
                { value: 'direct_deposit', label: 'Direct Deposit' },
                { value: 'deft', label: 'DEFT' },
            ],

            paymentTermsOptions: [
                { value: 'on_receipt', label: 'On Receipt' },
                { value: 'net_7', label: 'Net 7' },
                { value: 'net_15', label: 'Net 15' },
                { value: 'net_30', label: 'Net 30' },
            ],

            priorityOptions: [
                { value: 'L', label: 'L — Low' },
                { value: 'M', label: 'M — Medium' },
                { value: 'H', label: 'H — High' },
                { value: 'C', label: 'C — Critical' },
            ],

            shipDateConstraints: null,
            shipDateRefetchTimer: null,
        }
    },

    methods: {
        scheduleShipDateRefetch() {
            if (this.shipDateRefetchTimer) {
                clearTimeout(this.shipDateRefetchTimer);
            }
            this.shipDateRefetchTimer = setTimeout(() => {
                this.refetchShipDateConstraints();
            }, 200);
        },

        async refetchShipDateConstraints() {
            if (!this.form.customer_id || !this.form.order_date || this.form.status === 2) {
                return;
            }
            try {
                const orderItems = (this.form.order_items || []).map((i) => ({
                    product_id: i.product_id ?? i.product?.id,
                })).filter((row) => row.product_id != null);

                this.shipDateConstraints = await fetchStaffShipDateConstraints({
                    customer_id: this.form.customer_id,
                    order_date: DateTime.fromJSDate(this.form.order_date).toFormat('yyyy-LL-dd'),
                    shipping_method: this.form.shipping_method,
                    override_ship_date: !!this.form.override_ship_date,
                    is_editing_order: this.form.id !== null,
                    order_items: orderItems,
                });
            } catch (e) {
                console.error(e);
            }
        },

        checkDeliveryDays() {
            if(this.form.ship_date != null && this.form.shipping_method === 'delivery')
            {
                let day_selected = this.form.ship_date.toLocaleString('en-US', { weekday: 'long'});
                let deliveryDays = this.customerDeliveryDaysUnion;
                const ymd = DateTime.fromJSDate(this.form.ship_date).toFormat('yyyy-LL-dd');
                const blocked = this.shipDateConstraints?.blocked_holiday_dates || this.holidays;
                let isHoliday = blocked.includes(ymd);

                if (this.selectedCustomer.type === 'School') {
                    isHoliday = false;
                }

                if(isHoliday || !deliveryDays.toLowerCase().includes(day_selected.toLowerCase())) {
                    this.form.ship_date = null;
                }
            }
        },

        toggleOverrideDates() {
            this.form.override_ship_date = !this.form.override_ship_date;

            if(this.form.override_ship_date === false) {
                this.checkDeliveryDays();
            }

            return this.form.override_ship_date;
        },

        formatDate(date) {
            return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
        },

        applyCustomerRoutingDefaults(customer) {
            const typeSkill = customer.type === 'School' ? 'SCH' : 'WHO';
            const customerSkills = Array.isArray(customer.skills) ? customer.skills : [];
            this.form.skills = [...new Set([typeSkill, ...customerSkills].filter(Boolean))];
            this.form.vehicle_features = Array.isArray(customer.vehicle_features)
                ? [...customer.vehicle_features]
                : [];
            this.form.priority = 'M';
        },
    },

    computed: {
        skillOptions() {
            return this.page.props.value.optimorouteSkillOptions || ['SCH', 'WHO', 'Retail', 'EVE'];
        },
        vehicleFeatureOptions() {
            return this.page.props.value.optimorouteVehicleTypeOptions || ['BOXTRUCK'];
        },

        orderWillBeSplit () {
            if (
                this.form.id !== null ||
                this.form.override_ship_date ||
                this.form.ship_date === null ||
                !this.shipDateConstraints ||
                !(this.hasSpecialOrderItems)
            ) {
                return false;
            }

            const shipDate = DateTime.fromJSDate(this.form.ship_date).startOf('day');
            const minSpecialOrderDate = DateTime.fromISO(this.shipDateConstraints.special_order_min_date).startOf('day');

            if (minSpecialOrderDate <= shipDate) {
                return false;
            }

            return true;
        },

        minDate () {
            if (this.form.override_ship_date) {
                return null;
            }

            if (this.shipDateConstraints && this.shipDateConstraints.min_date) {
                return DateTime.fromISO(this.shipDateConstraints.min_date).toJSDate();
            }

            return DateTime.now().toJSDate();
        },

        maxDate () {
            if (this.form.override_ship_date) {
                return null;
            }

            if (this.shipDateConstraints && this.shipDateConstraints.max_date) {
                return DateTime.fromISO(this.shipDateConstraints.max_date).toJSDate();
            }

            let today = new Date();
            return new Date(today.setMonth(today.getMonth() + 3));
        },

        selectedCustomer () {
            return this.form.customer_id == null ? null : this.customers.find(i => i.id == this.form.customer_id);
        },

        customerDeliveryDaysUnion () {
            const customer = this.selectedCustomer;
            if (!customer) {
                return '';
            }

            if (customer.delivery_days) {
                return String(customer.delivery_days);
            }

            const schedules = customer.delivery_schedules || [];
            const days = [];
            schedules.forEach((row) => {
                const rowDays = typeof row.delivery_days === 'string'
                    ? row.delivery_days.split(',').map((d) => d.trim()).filter(Boolean)
                    : (row.delivery_days || []);
                rowDays.forEach((day) => days.push(day));
            });

            return [...new Set(days)].join(',');
        },

        selectedCustomerPayment () {
            return this.selectedCustomer == null ? false : this.selectedCustomer.payment_method == 'credit_card' ? true : false;
        },

        customerDisabledDates () {
            if (this.selectedCustomer != null && !this.form.override_ship_date && this.shipDateConstraints?.disabled_dates) {
                return this.shipDateConstraints.disabled_dates.map((d) => DateTime.fromISO(d).toJSDate());
            }

            return [];
        },

        dairyCategory() {
            let category = this.categories.find(c => c.id == 96);

            if(category === undefined) return null;

            return category.id;
        },

        hasSpecialOrderItems() {
            return this.form.order_items.filter(i => i.product.special_order === true).length > 0;
        },

        hasDairyItems() {
            if(!this.dairyCategory) return false;

            return this.form.order_items.filter(i => i.product.category_id === this.dairyCategory).length > 0;
        },
    },

    watch: {
        selectedCustomer(newVal, oldVal) {
            if(newVal != null)
            {
                // Prevent Inertia validation error from resetting the customer form
                let isSameCustomer = oldVal != null && oldVal.id === newVal.id;

                if(oldVal == null || !isSameCustomer) {
                    this.form.notes = newVal.order_notes;
                    this.form.shipping_method = newVal.default_shipping_method;
                    this.form.po_num = newVal.po_num;
                    // Apply customer routing defaults on create, or when switching customers on edit.
                    if (this.form.id == null || (oldVal != null && !isSameCustomer)) {
                        this.applyCustomerRoutingDefaults(newVal);
                    }
                    this.checkDeliveryDays();
                    this.scheduleShipDateRefetch();
                }
            }
            else
            {
                this.form.ship_date = null;
                this.form.notes = null;
                this.form.shipping_method = 'delivery';
                this.form.skills = [];
                this.form.vehicle_features = [];
                this.form.priority = 'M';
                this.shipDateConstraints = null;
            }
        },

        'form.shipping_method'() {
            this.scheduleShipDateRefetch();
        },

        'form.order_date'() {
            this.scheduleShipDateRefetch();
        },

        'form.override_ship_date'() {
            this.scheduleShipDateRefetch();
        },

        'form.order_items': {
            handler() {
                this.scheduleShipDateRefetch();
            },
            deep: true,
        },
    },

    mounted () {
        if (this.form.id === null) {
            this.$refs.customer.input.focus();
        }
        this.scheduleShipDateRefetch();
    }
}
</script>

<style>
.dp-custom-input {
    @apply h-8;
}
.box>* {
    flex: 1 1 300px;
}
</style>

<style scoped>
input:active.disabled,
input:focus.disabled,
input:hover.disabled,
input.disabled {
    @apply bg-gray-300;
}
</style>
